import React from 'react';
import { Box, Chip, Grid, TextField, Typography } from '@mui/material';

import { useZoneFeedbackFormManager } from './zone-feedback-form.hook';
import { IZone } from '../../../models';
import { theme } from '../../../configs';
import { PhoneNumberMask } from '../../../components';

interface ZoneFeedbackFormProps {
  zone: IZone;
  next?: (data?: any) => void;
}

export const ZoneFeedbackForm = React.forwardRef(({ zone, next }: ZoneFeedbackFormProps, ref) => {
  const [{ formManager }] = useZoneFeedbackFormManager({ zoneId: zone.id, next });
  const primaryBrandColor = zone.building?.organization?.primary_brand_color ?? theme.palette.text.primary;
  const cleanOptions = ['cleaned', 'refilled', 'sanitized', 'disinfected', 'inspected', 'serviced'];

  React.useImperativeHandle(ref, () => ({ onSubmit: () => formManager.handleSubmit() }), []);

  const optionBackgroundColor = (option: string): string => {
    const selectedServices = formManager.values.services;
    return selectedServices.includes(option) ? primaryBrandColor : `${primaryBrandColor}30`;
  };

  const handleServiceOptionClick = (option: string): void => {
    const selectedServices = formManager.values.services;
    const optionIndex = selectedServices.findIndex((o) => o === option);

    if (optionIndex > -1) {
      selectedServices.splice(optionIndex, 1);
    } else {
      selectedServices.push(option);
    }

    formManager.setFieldValue('services', selectedServices);
  };

  return (
    <Box
      sx={{
        paddingY: 2,
      }}
    >
      <form onSubmit={formManager.handleSubmit}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography style={{ fontWeight: 'bold', fontSize: 14 }}>Zone Feedback</Typography>
          <Typography style={{ fontSize: 14 }}>This area needs to be...</Typography>
        </Box>

        <Box width="270px" sx={{ margin: '15px auto' }}>
          <Grid container spacing={1} justifyContent="center" alignItems="flex-start">
            {cleanOptions.map((option, index) => (
              <Grid item xs="auto" key={index}>
                <Chip
                  label={option}
                  style={{ backgroundColor: optionBackgroundColor(option) }}
                  onClick={() => handleServiceOptionClick(option)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        <TextField
          placeholder="Name *"
          variant="filled"
          fullWidth
          margin="dense"
          onChange={formManager.handleChange('name')}
          error={!!(formManager.errors.name && formManager.touched.name)}
          helperText={formManager.touched.name ? formManager.errors.name : null}
          value={formManager.values.name || ''}
        />

        <TextField
          placeholder="Email *"
          variant="filled"
          fullWidth
          margin="dense"
          onChange={formManager.handleChange('email')}
          error={!!(formManager.errors.email && formManager.touched.email)}
          helperText={formManager.touched.email ? formManager.errors.email : null}
          value={formManager.values.email || ''}
        />

        <TextField
          placeholder="Phone number"
          variant="filled"
          fullWidth
          margin="dense"
          value={formManager.values.phone}
          name="phone"
          InputProps={{
            inputComponent: PhoneNumberMask as any,
          }}
          onChange={formManager.handleChange('phone')}
          error={!!(formManager.errors.phone && formManager.touched.phone)}
          helperText={formManager.touched.phone ? formManager.errors.phone : null}
        />

        <Typography variant="subtitle2" sx={{ textAlign: 'center', mt: 1 }}>
          Additional comments?
        </Typography>
        <TextField
          placeholder="Write your comment here"
          variant="filled"
          fullWidth
          multiline
          minRows={4}
          onChange={formManager.handleChange('message')}
          error={!!(formManager.errors.message && formManager.touched.message)}
          helperText={formManager.touched.message ? formManager.errors.message : null}
          value={formManager.values.message || ''}
        />
      </form>
    </Box>
  );
});
